import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PaginationParams } from '../types/common';
import { CreateTrainingModuleDto, UpdateTrainingModuleDto } from '../types/dtos/training';
import { createTrainingModuleService } from '../services/training-module.service';

const TRAINING_MODULES_KEY = 'training-modules';

export const useTrainingModules = (
  organizationId: string, 
  trainingId: string, 
  params?: PaginationParams
) => {
  const moduleService = createTrainingModuleService(organizationId, trainingId);

  return useQuery({
    queryKey: [TRAINING_MODULES_KEY, organizationId, trainingId, params],
    queryFn: () => moduleService.findAll(params).then((res) => res.data),
    enabled: !!organizationId && !!trainingId
  });
};

export const useTrainingModule = (
  organizationId: string, 
  trainingId: string, 
  moduleId: string
) => {
  const moduleService = createTrainingModuleService(organizationId, trainingId);

  return useQuery({
    queryKey: [TRAINING_MODULES_KEY, organizationId, trainingId, moduleId],
    queryFn: () => moduleService.findById(moduleId).then((res) => res.data),
    enabled: !!organizationId && !!trainingId && !!moduleId
  });
};

export const useCreateTrainingModule = (organizationId: string, trainingId: string) => {
  const queryClient = useQueryClient();
  const moduleService = createTrainingModuleService(organizationId, trainingId);

  return useMutation({
    mutationFn: (data: CreateTrainingModuleDto) => 
      moduleService.create(data).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAINING_MODULES_KEY, organizationId, trainingId] });
    }
  });
};

export const useUpdateTrainingModule = (organizationId: string, trainingId: string) => {
  const queryClient = useQueryClient();
  const moduleService = createTrainingModuleService(organizationId, trainingId);

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTrainingModuleDto }) =>
      moduleService.update(id, data).then((res) => res.data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [TRAINING_MODULES_KEY, organizationId, trainingId] });
      queryClient.invalidateQueries({ queryKey: [TRAINING_MODULES_KEY, organizationId, trainingId, id] });
    }
  });
};

export const useDeleteTrainingModule = (organizationId: string, trainingId: string) => {
  const queryClient = useQueryClient();
  const moduleService = createTrainingModuleService(organizationId, trainingId);

  return useMutation({
    mutationFn: (id: string) => moduleService.delete(id).then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TRAINING_MODULES_KEY, organizationId, trainingId] });
    }
  });
};

